import {
  Configuration,
  ConfigurationOption,
  ConfigurationOptionWithSubgroup,
} from '@/types/models';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { z } from 'zod';
import { axiosInstance, axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { configurationSchema } from '@/models/configurationSchema';
import {
  configurationOptionSchema,
  configurationOptionWithSubgroupSchema,
} from '@/models/configurationOptionSchema';
import { ThunkConfig } from '@/types/store';
import { getError } from '@/utils/error-report/getError';
import { apiPath } from '../apiPath';
import {
  CONFIGURATION_FETCHING_ERROR_PREFIX,
  CONFIGURATION_OPTIONS_FETCHING_ERROR_PREFIX,
} from './configuration.constants';

export const getConfigurationOptions = createAsyncThunk<
  ConfigurationOption[] | undefined,
  void,
  ThunkConfig
>('configuration/getConfigurationOptions', async () => {
  try {
    const response = await axiosInstance.get<ConfigurationOption[]>(
      apiPath.getConfigurationOptions(),
    );

    const isDataValid = validateDataUsingZodSchema(
      response.data,
      z.array(configurationOptionSchema),
    );

    return isDataValid ? response.data : undefined;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: CONFIGURATION_OPTIONS_FETCHING_ERROR_PREFIX }));
  }
});

export const getConfigurationOptionsWithSubgroup = createAsyncThunk<
  ConfigurationOptionWithSubgroup[] | undefined,
  void,
  ThunkConfig
>('configuration/getConfigurationOptionsWithSubgroup', async () => {
  try {
    const response = await axiosInstanceNewAPI.get<ConfigurationOptionWithSubgroup[]>(
      apiPath.getConfigurationOptions(),
    );

    const isDataValid = validateDataUsingZodSchema(
      response.data,
      z.array(configurationOptionWithSubgroupSchema),
    );

    return isDataValid ? response.data : undefined;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: CONFIGURATION_OPTIONS_FETCHING_ERROR_PREFIX }));
  }
});

export const getConfiguration = createAsyncThunk<Configuration | undefined, void, ThunkConfig>(
  'configuration/getConfiguration',
  async () => {
    try {
      const response = await axiosInstance.get<Configuration>(apiPath.getConfiguration());

      const isDataValid = validateDataUsingZodSchema(response.data, configurationSchema);

      return isDataValid ? response.data : undefined;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: CONFIGURATION_FETCHING_ERROR_PREFIX }));
    }
  },
);

export const updateConfiguration = createAsyncThunk<
  boolean,
  { id: string; value: string },
  ThunkConfig
>('configuration/updateConfiguration', async ({ id, value }) => {
  try {
    await axiosInstance.patch(
      apiPath.updateConfiguration(id),
      { option: { type: id, value } },
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
    return true;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: 'Failed to update plugin' }));
  }
});

export const registerMachine = createAsyncThunk<boolean, void, ThunkConfig>(
  'minervanet/registerMachine',
  async () => {
    try {
      await axiosInstance.post(
        apiPath.registerMachine(),
        {},
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      );
      return true;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: 'Failed to register machine' }));
    }
  },
);

export const unRegisterMachine = createAsyncThunk<boolean, void, ThunkConfig>(
  'minervanet/unRegisterMachine',
  async () => {
    try {
      await axiosInstance.post(
        apiPath.unregisterMachine(),
        {},
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      );
      return true;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: 'Failed to unregister machine' }));
    }
  },
);
